import { useStateLocalStorage } from "@dotlegal/dotlegal-ui-components";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { LatestAuditsSubmittedWidgetProps } from "./LatestAuditsSubmittedWidget";
import { LatestInternalAssessmentSubmissionViewModel, LatestLegalEntityAuditSubmissionViewModel } from "./LatestAuditsSubmittedWidget.types";
import { NavigationMenuItem } from "../../navigationMenuSettings/NavigationMenuSettings.types";
import { isNavigationMenuItemHidden } from "../../navigationMenuSettings/navigationMenuItems";

import { useDotLegalQuery, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function useLatestAuditsSubmittedWidget(props: LatestAuditsSubmittedWidgetProps) {
    const { permissions, hiddenNavigationMenuItems } = useUserContext();
    const { vendorManagementFeatures } = usePlanContext();

    const isAuditsEnabled =
        vendorManagementFeatures.hasVendorManagement &&
        permissions.canManageAuditGroups &&
        !isNavigationMenuItemHidden(hiddenNavigationMenuItems, NavigationMenuItem.ExternalAudits);
    const isInternalAssessmentsEnabled =
        vendorManagementFeatures.hasVendorManagement &&
        permissions.canManageInternalAssessment &&
        !isNavigationMenuItemHidden(hiddenNavigationMenuItems, NavigationMenuItem.InternalAssessments);

    const defaultAssessmentsToShow = isAuditsEnabled ? false : isInternalAssessmentsEnabled;

    const localStorageKey = "dashboard-latest-submissions";
    const storageValue = localStorage.getItem(localStorageKey);
    if (
        (storageValue === "false" && !isAuditsEnabled) ||
        (!defaultAssessmentsToShow && !isAuditsEnabled) ||
        (defaultAssessmentsToShow && !isInternalAssessmentsEnabled) ||
        (storageValue === "true" && !isInternalAssessmentsEnabled)
    ) {
        localStorage.removeItem(localStorageKey);
    }

    const disableSwitch = (!defaultAssessmentsToShow && !isInternalAssessmentsEnabled) || (defaultAssessmentsToShow && !isAuditsEnabled);
    const [showInternalAssessments, setShowInternalAssessments] = useStateLocalStorage(localStorageKey, defaultAssessmentsToShow);

    const latestAuditsQuery = useDotLegalQueryWithKey<Array<LatestLegalEntityAuditSubmissionViewModel>>(
        `audits${props.onlyUserSpecific}`,
        `/legalEntityAudit/dashboard/latestSubmissions?onlyUserSpecific=${props.onlyUserSpecific}&forGroupEntity=${props.selectedGroupCompany}`,
        {
            enabled: isAuditsEnabled,
        }
    );

    const latestAssessmentsQuery = useDotLegalQueryWithKey<Array<LatestInternalAssessmentSubmissionViewModel>>(
        `assessments${props.onlyUserSpecific}`,
        `/internalAssessments/dashboard/latestSubmissions?onlyUserSpecific=${props.onlyUserSpecific}&forGroupEntity=${props.selectedGroupCompany}`,
        {
            enabled: isInternalAssessmentsEnabled,
        }
    );

    return {
        isLoading: latestAuditsQuery.isLoading || latestAssessmentsQuery.isLoading,
        isAuditsEnabled,
        isInternalAssessmentsEnabled,
        hasVendorManagementAccess: vendorManagementFeatures.hasVendorManagement,
        showInternalAssessments,
        setShowInternalAssessments,
        latestAssessments: latestAssessmentsQuery.data ?? [],
        latestAudits: latestAuditsQuery.data ?? [],
        disableSwitch,
    };
}
